import { HttpClient } from '@angular/common/http';
import { Component, Input, OnInit, Optional } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PrintService } from '../../print.service';
import { EmailModalComponent } from './email-modal/email-modal.component';
import { TranslateService } from '@ngx-translate/core';
import { Step2 } from 'src/main/webapp/assets/models/iter.model';
import { parseLocalDate } from '../../services/utils/date-utils';

// Aggiungi questa interfaccia all'inizio del file
interface Tappa {
  city_name: string;
  city_title?: string;
  start_date: string;
  end_date: string;
  days: any[];
  services: {
    hotels: any[];
    flights: any[];
    cars: any[];
    transfers: any[];
    optionals: any[];
    escorteds: any[];
  };
  serviceOrder: { type: string; service: any }[];
}

// tslint:disable-next-line:completed-docs
@Component({
  selector: 'app-print',
  styleUrls: ['./print.component.scss'],
  templateUrl: './print.component.html'
})
export class PrintComponent implements OnInit {
  @Input() public data: any;
  public amountApis: any;
  public advInformations: any;
  public citiesImages: any;
  public allDays: any;
  public consolidator = '';
  public days: any[] = [];
  public extra: any;
  public footerText = '';
  public iter: any;
  public lastUpdate = '';
  public logoAdv = '';
  public logoTourOperator = '';
  public tenant: any;
  public noImageUrl: any;
  public state: any = sessionStorage.getItem('state');

  @Input() public isModal: boolean = true;

  // Aggiungi questa proprietà alla classe
  public tappe: Tappa[] = [];

  // Totali calcolati (come nel summary component)
  public totalGroundService: number = 0;
  public totalFlightsService: number = 0;
  public totalFeeInsuranceService: number = 0;

  // Aggiungi queste proprietà alla classe
  public totalGroundNoOptional: number = 0;
  public totalFlightsTaxService: number = 0;

  public mapImageError = false;
  public totalActivities: number = 0;
  public transfersSuggestedTotal: number = 0;
  public optionalsSuggestedTotal: number = 0;

  public locationDetailsPosition: 'header' | 'footer' | null = null;
  public locationDetails: { name: string; photo: string; description: string }[] = [];

  public showGeneralNotes: boolean = false;
  public generalNotes: any[] = [];
  public countryNotes: any[] = [];
  public supplierNotes: any[] = [];

  // Stringa di parametri `style=` (Static Maps) dello stile silver, risolta da
  // MTP_PRINT_MAPS.mapStyles (inline) o MTP_PRINT_MAPS.mapStyleUrl. Vuota se assente.
  private mapStyleParams = '';

  constructor(
    @Optional() public activeModal: NgbActiveModal,
    private printService: PrintService,
    private modalService: NgbModal,
    private translateService: TranslateService,
    private http: HttpClient
  ) { }

  // Modifica il metodo ngOnInit per includere il raggruppamento per tappe
  public ngOnInit(): void {
    if (this.data) {
      this.iter = this.data.iter;
      this.days = this.data.days;
      this.allDays = this.data.allDays;
      this.extra = this.data.extra;
      this.logoAdv = this.data.logoAdv;
      this.logoTourOperator = this.data.logoTourOperator;
      this.consolidator = this.data.consolidator;
      this.lastUpdate = this.data.lastUpdate;
      this.footerText = this.data.footerText;
      this.tenant = this.data.tenant;
      this.noImageUrl = this.data.noImageUrl;
      this.amountApis = this.data.amountApis;
      this.advInformations = this.data.advInformations;
      console.log(this.data);
      this.initializeNotes();
      this.calculateTotals();
      this.loadPrintStyleUrl();
    }

    // Stile silver della static map (opzionale): assente ⇒ stampa invariata.
    this.loadMapStyleParams();

    // Raggruppa i giorni per tappe
    this.tappe = this.createTappe();

    // Aggiungi gli escorted steps alle tappe
    this.addEscortedStepsToTappe();
    this.sortTappeServices();
    this.markEscortedHeaderVisibility();
    this.initializeLocationDetails();
  }

  private initializeLocationDetails(): void {
    const setting = this.tenant?.printSettings?.showLegacyLocationDetailsLayout;
    this.locationDetailsPosition = setting === 'header' || setting === 'footer' ? setting : null;
    this.locationDetails = [];
    if (!this.locationDetailsPosition) {
      return;
    }

    const byCity = new Map<string, { name: string; photo: string; description: string }>();
    for (const tappa of this.tappe) {
      const key = (tappa.city_name || '').toLowerCase();
      if (!key) {
        continue;
      }
      let entry = byCity.get(key);
      if (!entry) {
        entry = { name: tappa.city_title || tappa.city_name, photo: '', description: '' };
        byCity.set(key, entry);
      }
      for (const day of tappa.days || []) {
        if (!entry.photo && day.city_photo && this.data?.urlCitiesImages) {
          entry.photo = this.toSecureUrl(this.data.urlCitiesImages + day.city_photo);
        }
        if (!entry.description && day.city_description) {
          entry.description = day.city_description;
        }
      }
    }
    this.locationDetails = Array.from(byCity.values());
  }

  // Metodo per ottenere tutti i voli
  public getAllFlights(): any[] {
    if (!this.allDays?.flights) return [];

    // Ordina i voli per data e ora di partenza
    return this.allDays.flights.sort((a: any, b: any) => {
      const dateA = new Date(a.segments[0].departure.date + ' ' + a.segments[0].departure.hour);
      const dateB = new Date(b.segments[0].departure.date + ' ' + b.segments[0].departure.hour);
      return dateA.getTime() - dateB.getTime();
    });
  }

  // Metodo per ottenere la durata totale del volo
  public getFlightDuration(flight: any): string {
    return flight.totalDuration || flight.totalDurationWithLand || '';
  }

  // Restituisce la compagnia del segmento visualizzato nella stampa.
  public getAirlineInfo(segment: any): string {
    return segment?.marketingAirline?.name || '';
  }

  // Restituisce il codice/numero appartenente al segmento visualizzato.
  public getFullFlightNumber(segment: any): string {
    const number = segment && segment.flightNumber != null
      ? String(segment.flightNumber).trim()
      : '';
    if (!number) {
      return '';
    }

    const code = segment?.marketingAirline?.code || '';
    return code ? `${code}${number}` : number;
  }

  // La stampa usa la stessa sorgente e lo stesso criterio del tab Itinerario/Voli.
  public hasFlightBaggageInfo(flight: any): boolean {
    const baggageInfo = flight?.segments?.[0]?.baggageInfo;
    return !!baggageInfo && baggageInfo.length > 0;
  }

  public getFlightBaggage(flight: any): string {
    return this.hasFlightBaggageInfo(flight) ? flight.segments[0].baggage || '' : '';
  }

  public getVehicleClassDescription(vehicle: any): string {
    const carClass = vehicle?.carClass ? String(vehicle.carClass).trim() : '';
    const promotionDescription = vehicle?.promotionDescription
      ? String(vehicle.promotionDescription).trim()
      : '';

    return [carClass, promotionDescription].filter(Boolean).join(' + ');
  }

  // Metodo per ottenere la durata di un singolo segmento
  public getSegmentDuration(segment: any): string {
    return segment?.duration || '';
  }

  // Metodo per calcolare la durata dello scalo tra due segmenti
  public calculateLayoverDuration(segments: any[], index: number): string {
    if (!segments || index < 1 || !segments[index - 1] || !segments[index]) {
      return '';
    }

    const prevArrival = new Date(segments[index - 1].arrival.date + 'T' + segments[index - 1].arrival.hour);
    const currDeparture = new Date(segments[index].departure.date + 'T' + segments[index].departure.hour);

    const totalMinutes = Math.round((currDeparture.getTime() - prevArrival.getTime()) / (1000 * 60));

    if (totalMinutes <= 0) {
      return '';
    }

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return `${hours} h ${minutes} min`;
  }

  // Metodo per ottenere il testo dell'alert di scalo nella lingua corrente
  public getLayoverAlertText(segments: any[], index: number): string {
    if (!segments || index < 1 || !segments[index - 1]) {
      return '';
    }

    const airport = segments[index - 1].arrival.airportName || '';
    const duration = this.calculateLayoverDuration(segments, index);

    return this.translateService.instant('ITINERARY.TRANSPORT.FLIGHT.LAYOVER_ALERT', {
      airport,
      duration
    });
  }

  // ===== MAP SECTION METHODS =====

  private getMapsConfig(): any {
    return (window as any).MTP_PRINT_MAPS || {};
  }

  public shouldShowMap(): boolean {
    const config = this.getMapsConfig();
    if (config && typeof config.show === 'boolean') {
      return config.show;
    }
    return true;
  }

  public getMapSteps(): Step2[] {
    if (!this.iter?.steps) {
      return [];
    }

    return this.iter.steps.filter((step: Step2) =>
      step.latitude &&
      step.longitude &&
      step.latitude.trim() !== '' &&
      step.longitude.trim() !== ''
    );
  }

  public getMapHeight(): string {
    return this.getMapsConfig().height || '400px';
  }

  public getMapZoom(): number | null {
    const zoom = this.getMapsConfig().zoom;
    if (zoom === null || zoom === undefined || zoom === '') {
      return null;
    }
    const parsed = Number(zoom);
    if (isNaN(parsed)) {
      return null;
    }
    return Math.max(1, Math.min(21, parsed));
  }

  public getMapCenter(): { lat: number; lng: number } | null {
    const steps = this.getMapSteps();
    if (steps.length === 0) {
      return null;
    }

    const center = this.getMapsConfig().center;
    if (center && center.lat && center.lng) {
      const lat = parseFloat(center.lat);
      const lng = parseFloat(center.lng);
      if (!isNaN(lat) && !isNaN(lng)) {
        return { lat, lng };
      }
    }

    let totalLat = 0;
    let totalLng = 0;
    let count = 0;

    for (const step of steps) {
      const lat = parseFloat(step.latitude);
      const lng = parseFloat(step.longitude);
      if (!isNaN(lat) && !isNaN(lng)) {
        totalLat += lat;
        totalLng += lng;
        count++;
      }
    }

    if (count === 0) {
      return null;
    }

    return { lat: totalLat / count, lng: totalLng / count };
  }

  public normalizeMapColor(color: string): string {
    if (!color) {
      return color;
    }
    const trimmed = color.trim();
    if (trimmed.startsWith('#')) {
      return '0x' + trimmed.substring(1);
    }
    return trimmed;
  }

  public normalizeMapColorForSvg(color: string): string {
    if (!color) {
      return color;
    }
    const trimmed = color.trim();
    if (trimmed.startsWith('0x')) {
      return '#' + trimmed.substring(2);
    }
    if (trimmed.startsWith('#')) {
      return trimmed;
    }
    return trimmed;
  }

  // Risolve i parametri `style=` della static map a partire dalla configurazione
  // di stampa (MTP_PRINT_MAPS.mapStyles inline oppure mapStyleUrl). Asincrono:
  // la stringa risolta è poi riusata in modo sincrono da getStaticMapBaseUrl.
  private loadMapStyleParams(): void {
    const config = this.getMapsConfig();
    if (Array.isArray(config.mapStyles) && config.mapStyles.length > 0) {
      this.mapStyleParams = this.buildMapStyleParams(config.mapStyles);

      return;
    }

    const url = config.mapStyleUrl;
    if (typeof url === 'string' && url) {
      this.http.get<any[]>(url).subscribe(
        styles => { this.mapStyleParams = this.buildMapStyleParams(styles); },
        () => { this.mapStyleParams = ''; }
      );
    }
  }

  // Concatena ogni MapTypeStyle in un parametro `style=` ripetuto, uniti da `&`.
  private buildMapStyleParams(styles: any): string {
    if (!Array.isArray(styles)) {
      return '';
    }

    return styles
      .map(style => this.buildMapStyleParam(style))
      .filter(param => param !== '')
      .join('&');
  }

  // Traduce un MapTypeStyle in `style=feature:<ft>|element:<et>|<styler>:<val>`.
  private buildMapStyleParam(style: any): string {
    if (!style) {
      return '';
    }
    const parts: string[] = [];
    if (style.featureType) {
      parts.push(`feature:${style.featureType}`);
    }
    if (style.elementType) {
      parts.push(`element:${style.elementType}`);
    }
    const stylers = Array.isArray(style.stylers) ? style.stylers : [];
    for (const styler of stylers) {
      if (!styler || typeof styler !== 'object') {
        continue;
      }
      for (const key of Object.keys(styler)) {
        parts.push(`${key}:${this.normalizeStyleValue(styler[key])}`);
      }
    }

    return parts.length > 0 ? `style=${parts.join('|')}` : '';
  }

  private normalizeStyleValue(value: any): string {
    return typeof value === 'string' && value.startsWith('#')
      ? this.normalizeMapColor(value)
      : String(value);
  }

  public getMapLineColor(): string {
    return this.normalizeMapColorForSvg(this.getMapsConfig().lineColor || '0xE74C3C');
  }

  public getMapLineColorForUrl(): string {
    return this.normalizeMapColor(this.getMapsConfig().lineColor || '0xE74C3C');
  }

  public getMapPinColor(): string {
    return this.normalizeMapColorForSvg(this.getMapsConfig().pinColor || 'red');
  }

  public getMapPinColorForUrl(): string {
    return this.normalizeMapColor(this.getMapsConfig().pinColor || 'red');
  }

  public getMapMarkerTextColor(): string {
    return this.normalizeMapColorForSvg(this.getMapsConfig().markerTextColor || 'white');
  }

  public getMapMarkerRadius(): number {
    const size = this.getMapsConfig().markerSize;
    switch (size) {
      case 'tiny': return 8;
      case 'small': return 10;
      case 'mid': return 12;
      case 'normal': return 14;
      default: return 14;
    }
  }

  public getMapMarkerFontSize(): number {
    const size = this.getMapsConfig().markerSize;
    switch (size) {
      case 'tiny': return 8;
      case 'small': return 9;
      case 'mid': return 10;
      case 'normal': return 12;
      default: return 12;
    }
  }

  public getMapPathWeight(): number {
    const weight = this.getMapsConfig().pathWeight;
    if (weight === null || weight === undefined || weight === '') {
      return 3;
    }
    const parsed = Number(weight);
    if (isNaN(parsed)) {
      return 3;
    }
    return Math.max(1, Math.min(20, parsed));
  }

  public getMapType(): string {
    const type = this.getMapsConfig().mapType;
    const validTypes = ['roadmap', 'satellite', 'hybrid', 'terrain'];
    if (type && validTypes.includes(type)) {
      return type;
    }
    return 'roadmap';
  }

  public getMapFormat(): string {
    const format = this.getMapsConfig().format;
    const validFormats = ['png', 'png32', 'gif', 'jpg', 'jpg-baseline'];
    if (format && validFormats.includes(format)) {
      return format;
    }
    return 'png';
  }

  public getStaticMapUrl(): string {
    // La mappa statica viene usata solo come sfondo: marker e percorso sono
    // sovrapposti via SVG per poter mostrare numeri a più cifre (10, 11, ...).
    return this.getStaticMapBaseUrl();
  }

  public getStaticMapBaseUrl(): string {
    const steps = this.getMapSteps();
    if (steps.length === 0) {
      return '';
    }

    const apiKey = this.tenant?.GoogleMapKey;
    if (!apiKey) {
      return '';
    }

    const size = '800x400';
    const scale = '2';
    const mapType = this.getMapType();
    const format = this.getMapFormat();

    let url = `https://maps.googleapis.com/maps/api/staticmap?size=${size}&scale=${scale}&maptype=${mapType}&format=${format}&key=${apiKey}`;

    // Stile di sfondo (silver per CREO): parametri `style=` aggiunti solo se configurati.
    if (this.mapStyleParams) {
      url += `&${this.mapStyleParams}`;
    }

    const zoom = this.getMapZoom();
    if (zoom !== null) {
      url += `&zoom=${zoom}`;
      const center = this.getMapCenter();
      if (center) {
        url += `&center=${center.lat},${center.lng}`;
      }
    } else {
      const fit = this.computeMapFit(steps);
      if (fit) {
        url += `&zoom=${fit.zoom}&center=${fit.centerLat},${fit.centerLng}`;
      }
    }

    return url;
  }

  public getMapOverlayViewBox(): string {
    return '0 0 800 400';
  }

  public getMapSvgPathPoints(): string {
    const positions = this.getMapMarkerPositions();
    return positions.map(p => `${p.x},${p.y}`).join(' ');
  }

  public getMapMarkerPosition(step: Step2): { x: number; y: number } | null {
    const steps = this.getMapSteps();
    const index = steps.indexOf(step);
    const positions = this.getMapMarkerPositions();
    return positions[index] || null;
  }

  public getMapMarkerPositions(): { x: number; y: number }[] {
    const steps = this.getMapSteps();
    if (steps.length === 0) {
      return [];
    }

    const zoom = this.getMapZoom();
    let center: { lat: number; lng: number } | null;

    if (zoom !== null) {
      center = this.getMapCenter();
    } else {
      const fit = this.computeMapFit(steps);
      center = fit ? { lat: fit.centerLat, lng: fit.centerLng } : null;
    }

    if (!center) {
      return [];
    }

    const effectiveZoom = zoom !== null ? zoom : (this.computeMapFit(steps)?.zoom || 10);
    return steps.map(step => this.latLngToPixel(
      parseFloat(step.latitude),
      parseFloat(step.longitude),
      center!.lat,
      center!.lng,
      effectiveZoom,
      800,
      400
    ));
  }

  private computeMapFit(steps: Step2[]): { zoom: number; centerLat: number; centerLng: number } | null {
    if (steps.length === 0) {
      return null;
    }

    const coords = steps
      .map(s => ({ lat: parseFloat(s.latitude), lng: parseFloat(s.longitude) }))
      .filter(c => !isNaN(c.lat) && !isNaN(c.lng));

    if (coords.length === 0) {
      return null;
    }

    let minLat = coords[0].lat;
    let maxLat = coords[0].lat;
    let minLng = coords[0].lng;
    let maxLng = coords[0].lng;

    for (const c of coords) {
      minLat = Math.min(minLat, c.lat);
      maxLat = Math.max(maxLat, c.lat);
      minLng = Math.min(minLng, c.lng);
      maxLng = Math.max(maxLng, c.lng);
    }

    const centerLat = (minLat + maxLat) / 2;
    const centerLng = (minLng + maxLng) / 2;

    const paddingFactor = 0.15;
    const width = 800;
    const height = 400;

    // Se c'è un solo punto o tutti coincidenti, usa uno zoom di default
    const lngSpan = maxLng - minLng;
    const latSpan = maxLat - minLat;

    if (lngSpan === 0 && latSpan === 0) {
      return { zoom: 10, centerLat, centerLng };
    }

    let zoomLng = Infinity;
    let zoomLat = Infinity;

    if (lngSpan > 0) {
      zoomLng = Math.log2((width * (1 - paddingFactor) * 360) / (256 * lngSpan));
    }

    if (latSpan > 0) {
      const yNormMax = this.mercatorYNormalized(maxLat);
      const yNormMin = this.mercatorYNormalized(minLat);
      const ySpan = Math.abs(yNormMax - yNormMin);
      if (ySpan > 0) {
        zoomLat = Math.log2((height * (1 - paddingFactor)) / (256 * ySpan));
      }
    }

    const zoom = Math.floor(Math.min(zoomLng, zoomLat));
    return {
      zoom: Math.max(1, Math.min(21, zoom)),
      centerLat,
      centerLng
    };
  }

  private latLngToPixel(
    lat: number,
    lng: number,
    centerLat: number,
    centerLng: number,
    zoom: number,
    width: number,
    height: number
  ): { x: number; y: number } {
    const worldWidth = 256 * Math.pow(2, zoom);

    const x = (lng + 180) / 360 * worldWidth;
    const y = this.mercatorYNormalized(lat) * worldWidth;

    const centerX = (centerLng + 180) / 360 * worldWidth;
    const centerY = this.mercatorYNormalized(centerLat) * worldWidth;

    return {
      x: width / 2 + (x - centerX),
      y: height / 2 + (y - centerY)
    };
  }

  private mercatorYNormalized(lat: number): number {
    const latRad = this.degreesToRadians(lat);
    return (1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2;
  }

  private degreesToRadians(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  public onMapImageError(_event?: Event): void {
    this.mapImageError = true;
    console.warn('[PrintComponent] Google Static Maps image failed to load. Check that the Maps Static API is enabled for this API key and that billing is active.', this.getStaticMapUrl());
  }

  // Metodo per ottenere la classe del volo
  public getFlightClass(flight: any): string {
    if (flight.segments && flight.segments[0]) {
      return flight.segments[0].classFlight || 'N.D.';
    }
    return 'N.D.';
  }

  public getSupplierName(service: any): string {
    return service?.supplierName || service?.SupplierName || '';
  }

  // Metodo per ottenere il tipo di aereo
  public getAircraftType(flight: any): string {
    if (flight.segments && flight.segments[0]) {
      return flight.segments[0].airEquipType || 'N.C.';
    }
    return 'N.C.';
  }

  // Metodo per verificare se il tipo di aereo è valorizzato in modo significativo
  public hasAircraftType(flight: any): boolean {
    const type = this.getAircraftType(flight);
    if (!type) {
      return false;
    }
    const normalized = type.trim().toUpperCase();
    return !['', 'N.C.', 'NC', 'ND', 'N.D', 'N.D.', 'N/D'].includes(normalized);
  }

  // Metodo per formattare data e ora
  public formatDateTime(date: string, time: string): string {
    const lang = this.translateService.currentLang || 'it';
    const formattedDate = parseLocalDate(date).toLocaleDateString(lang);
    return `${formattedDate} ${time}`;
  }

  // Verifica se il giorno appartiene a un codice località (city_code) da ignorare
  private isIgnoredDay(day: any): boolean {
    if (!this.tenant?.ignoredIsoCodes || !day?.city_code) {
      return false;
    }
    const ignored = Array.isArray(this.tenant.ignoredIsoCodes)
      ? this.tenant.ignoredIsoCodes
      : this.tenant.ignoredIsoCodes.toString().split(',').map((c: string) => c.trim());
    return ignored.includes(day.city_code);
  }

  // Nuovo metodo per creare le tappe
  private createTappe(): Tappa[] {
    if (!this.days || this.days.length === 0) {
      return [];
    }

    const tappe: Tappa[] = [];
    let currentTappa: Tappa | null = null;

    this.days.forEach((day: any) => {
      if (this.isIgnoredDay(day)) {
        return;
      }

      const cityName = day.city_name;

      if (!cityName) {
        // Se non c'è city_name, aggiungi comunque i servizi alla tappa corrente
        if (currentTappa) {
          this.addDayServicesToTappa(currentTappa, day);
        }
        return;
      }

      // Se è una nuova città o la prima tappa
      if (!currentTappa || currentTappa.city_name !== cityName) {
        // Chiudi la tappa precedente se esiste
        if (currentTappa) {
          tappe.push(currentTappa);
        }

        // Crea una nuova tappa
        currentTappa = {
          city_name: cityName,
          city_title: day.city_title?.trim() || '',
          start_date: day.date,
          end_date: day.date,
          days: [day],
          services: {
            hotels: [],
            flights: [],
            cars: [],
            transfers: [],
            optionals: [],
            escorteds: []
          },
          serviceOrder: []
        };

        this.addDayServicesToTappa(currentTappa, day);
      } else {
        // Stessa città - aggiorna la data di fine e aggiungi i servizi
        if (!currentTappa.city_title && day.city_title?.trim()) {
          currentTappa.city_title = day.city_title.trim();
        }
        currentTappa.end_date = day.date;
        currentTappa.days.push(day);
        this.addDayServicesToTappa(currentTappa, day);
      }
    });

    // Aggiungi l'ultima tappa se esiste
    if (currentTappa) {
      tappe.push(currentTappa);
    }

    this.moveOptionalsToServiceCity(tappe);
    this.moveTransfersToServiceCity(tappe);

    return tappe;
  }

  private moveOptionalsToServiceCity(tappe: Tappa[]): void {
    tappe.forEach((sourceTappa: Tappa) => {
      const servicesToMove = sourceTappa.services.optionals.filter((optional: any) => {
        const serviceCity = optional.optionals?.find((service: any) => service.city_name)?.city_name;
        return serviceCity && !this.cityNamesMatch(sourceTappa.city_name, serviceCity);
      });

      servicesToMove.forEach((optional: any) => {
        const service = optional.optionals?.find((item: any) => item.city_name);
        const serviceCity = service?.city_name;
        const serviceDate = service?.date || optional.day_date;
        const targetTappa = tappe.find((tappa: Tappa) => {
          if (tappa === sourceTappa || !this.cityNamesMatch(tappa.city_name, serviceCity)) {
            return false;
          }
          return !serviceDate || (tappa.start_date <= serviceDate && serviceDate <= tappa.end_date);
        });

        if (targetTappa) {
          sourceTappa.services.optionals = sourceTappa.services.optionals.filter(service => service !== optional);
          targetTappa.services.optionals.push(optional);
        }
      });
    });
  }

  private cityNamesMatch(firstCity: string, secondCity: string): boolean {
    const first = firstCity.trim().toLowerCase();
    const second = secondCity.trim().toLowerCase();
    return first === second || first.includes(second) || second.includes(first);
  }

  private moveTransfersToServiceCity(tappe: Tappa[]): void {
    tappe.slice().forEach((sourceTappa: Tappa) => {
      [...sourceTappa.services.transfers].forEach((transfer: any) => {
        const service = transfer.optionals?.find((optional: any) => optional.cityBefore || optional.city_name);
        const serviceCity = service?.cityBefore || service?.city_name;
        if (!serviceCity || this.cityNamesMatch(sourceTappa.city_name, serviceCity)) {
          return;
        }

        const transferDate = service.date || transfer.day_date;
        if (!transferDate) {
          return;
        }

        let targetTappa = tappe.find((tappa: Tappa) => {
          if (tappa === sourceTappa || !this.cityNamesMatch(tappa.city_name, serviceCity)) {
            return false;
          }
          return tappa.start_date <= transferDate && transferDate <= tappa.end_date;
        });

        if (!targetTappa) {
          targetTappa = {
            city_name: serviceCity,
            start_date: transferDate,
            end_date: transferDate,
            days: [],
            services: {
              hotels: [],
              flights: [],
              cars: [],
              transfers: [],
              optionals: [],
              escorteds: []
            },
            serviceOrder: []
          };
          tappe.splice(tappe.indexOf(sourceTappa), 0, targetTappa);
        }

        sourceTappa.services.transfers = sourceTappa.services.transfers.filter(item => item !== transfer);
        targetTappa.services.transfers.push(transfer);
      });
    });
  }

  // Aggiorna il metodo addDayServicesToTappa per ESCLUDERE i voli
  private addDayServicesToTappa(tappa: Tappa, day: any): void {
    // Aggiungi hotel con le date
    if (day.hotels && day.hotels.length > 0) {
      day.hotels.forEach((hotel: any) => {
        tappa.services.hotels.push({
          ...hotel,
          day_date: day.date
        });
      });
    }

    // NON aggiungere i voli - li gestiamo separatamente nel Piano Voli
    // if (day.flights && day.flights.length > 0) { ... }

    // Aggiungi auto
    if (day.cars && day.cars.length > 0) {
      day.cars.forEach((car: any) => {
        tappa.services.cars.push({
          ...car,
          day_date: day.date
        });
      });
    }

    // Aggiungi transfer
    if (day.transfers && day.transfers.length > 0) {
      day.transfers.forEach((transfer: any) => {
        tappa.services.transfers.push({
          ...transfer,
          day_date: day.date
        });
      });
    }

    // Aggiungi optional
    if (day.optionals && day.optionals.length > 0) {
      day.optionals.forEach((optional: any) => {
        tappa.services.optionals.push({
          ...optional,
          day_date: day.date
        });
      });
    }

    // NON aggiungere gli escorted - li gestiamo separatamente con gli steps
    // if (day.escorteds && day.escorteds.length > 0) { ... }
  }

  // Nuovo metodo per aggiungere gli escorted steps alle tappe
  private addEscortedStepsToTappe(): void {
    if (!this.allDays?.escorteds || this.allDays.escorteds.length === 0) {
      return;
    }

    this.allDays.escorteds.forEach((escorted: any) => {
      if (escorted.optionals && escorted.optionals[0] && escorted.optionals[0].steps) {
        const steps = escorted.optionals[0].steps;

        steps.forEach((step: any) => {
          // Trova la tappa corrispondente al cityName dello step E con sovrapposizione date
          const stepDateFrom = new Date(step.dateFrom);
          const stepDateTo = new Date(step.dateTo);

          let targetTappa = this.tappe.find(tappa => {
            if (!this.cityNamesMatch(tappa.city_name, step.cityName)) {
              return false;
            }
            const tappaDateFrom = new Date(tappa.start_date);
            const tappaDateTo = new Date(tappa.end_date);
            return stepDateFrom <= tappaDateTo && stepDateTo >= tappaDateFrom;
          });

          if (!targetTappa && step.dateFrom === step.dateTo) {
            const dateTappa = this.tappe.find(tappa => {
              const tappaDateFrom = new Date(tappa.start_date);
              const tappaDateTo = new Date(tappa.end_date);
              return stepDateFrom >= tappaDateFrom && stepDateFrom <= tappaDateTo;
            });

            if (dateTappa) {
              targetTappa = {
                city_name: step.cityName,
                start_date: step.dateFrom,
                end_date: step.dateTo,
                days: [],
                services: {
                  hotels: [],
                  flights: [],
                  cars: [],
                  transfers: [],
                  optionals: [],
                  escorteds: []
                },
                serviceOrder: []
              };
              const insertIndex = this.tappe.findIndex((tappa: Tappa) => tappa.start_date >= step.dateFrom);
              this.tappe.splice(insertIndex === -1 ? this.tappe.length : insertIndex, 0, targetTappa);
            }
          }

          if (targetTappa) {
            // Crea un oggetto escorted step per la tappa
            const escortedStep = {
              ...escorted,
              step: step,
              day_date: step.dateFrom
            };

            targetTappa.services.escorteds.push(escortedStep);
          }
        });
      }
    });
  }

  private sortTappeServices(): void {
    this.tappe.forEach((tappa: Tappa) => {
      const sortByDateTime = (a: any, b: any, type: 'hotel' | 'car' | 'transfer' | 'optional' | 'escorted') => {
        const aKey = this.getPrintServiceSortKey(type, a);
        const bKey = this.getPrintServiceSortKey(type, b);
        return aKey.localeCompare(bKey);
      };

      tappa.services.hotels = [...(tappa.services.hotels || [])].sort((a, b) =>
        sortByDateTime(a, b, 'hotel')
      );
      tappa.services.cars = [...(tappa.services.cars || [])].sort((a, b) =>
        sortByDateTime(a, b, 'car')
      );
      tappa.services.transfers = [...(tappa.services.transfers || [])].sort((a, b) =>
        sortByDateTime(a, b, 'transfer')
      );
      tappa.services.optionals = [...(tappa.services.optionals || [])].sort((a, b) =>
        sortByDateTime(a, b, 'optional')
      );
      tappa.services.escorteds = [...(tappa.services.escorteds || [])].sort((a, b) =>
        sortByDateTime(a, b, 'escorted')
      );

      // Costruisce la serviceOrder interleaved (stesso approccio del portale):
      // tutti i servizi di terra vengono mischiati in un'unica sequenza cronologica.
      // Gli escorted (step del tour) entrano nella stessa sequenza.
      // L'ordine di push delle categorie replica il tie-break del portale
      // (itinerary.component.ts / recap.component.ts): a parità di data+ora
      // il tour precede gli altri servizi, poi il transfer prima dell'hotel.
      const entries: { type: string; service: any; key: string }[] = [];

      const pushEntries = (type: 'hotel' | 'car' | 'transfer' | 'optional' | 'escorted', services: any[]) => {
        services.forEach((service: any) => {
          entries.push({
            type,
            service,
            key: this.getPrintServiceSortKey(type, service)
          });
        });
      };

      pushEntries('escorted', tappa.services.escorteds);
      pushEntries('car', tappa.services.cars);
      pushEntries('transfer', tappa.services.transfers);
      pushEntries('hotel', tappa.services.hotels);
      pushEntries('optional', tappa.services.optionals);

      entries.sort((a, b) => a.key.localeCompare(b.key));
      tappa.serviceOrder = entries.map(e => ({ type: e.type, service: e.service }));
    });
  }

  private getPrintServiceSortKey(type: 'hotel' | 'car' | 'transfer' | 'optional' | 'escorted', service: any): string {
    const fallbackDate = '9999-99-99';
    const fallbackHour = '99:99';

    switch (type) {
      case 'hotel':
        return `${service?.checkin || service?.date || fallbackDate}T${service?.serviceHour || fallbackHour}`;
      case 'car':
        return `${service?.pickup?.date || service?.date || fallbackDate}T${service?.pickup?.hour || service?.serviceHour || fallbackHour}`;
      case 'transfer':
        return `${service?.optionals?.[0]?.date || service?.pickup?.date || service?.date || fallbackDate}T${service?.optionals?.[0]?.serviceHour || service?.pickup?.hour || service?.serviceHour || fallbackHour}`;
      case 'optional':
        return `${service?.optionals?.[0]?.date || service?.date || fallbackDate}T${service?.optionals?.[0]?.serviceHour || service?.serviceHour || fallbackHour}`;
      case 'escorted':
        return `${service?.step?.dateFrom || service?.day_date || fallbackDate}T${service?.step?.timeFrom || fallbackHour}`;
      default:
        return `${fallbackDate}T${fallbackHour}`;
    }
  }

  // Generic method to calculate total for any service type
  private calculateServiceTotalGeneric(serviceType: string): number {
    if (!this.allDays || !this.allDays[serviceType]) {
      return 0;
    }
    return this.allDays[serviceType].reduce((total: number, service: any) => {
      return total + this.sumOptionalsItems(service);
    }, 0);
  }

  // Metodo generico per calcolare il totale dei servizi standard
  private calculateServiceTotal(serviceType: string): number {
    return this.calculateServiceTotalGeneric(serviceType);
  }

  // Metodo generico per calcolare il totale dei servizi suggeriti
  private calculateSuggestedServiceTotal(serviceType: string): number {
    return this.calculateServiceTotalGeneric(serviceType);
  }

  // Helper to sum optionals items safely
  private sumOptionalsItems(service: any): number {
    if (service.optionals && Array.isArray(service.optionals) && service.optionals.length > 0 && service.optionals[0].items && Array.isArray(service.optionals[0].items)) {
      return service.optionals[0].items.reduce((itemsTotal: number, item: any) => {
        return itemsTotal + item.amountaftertax + (item.adv_markup ? item.adv_markup : 0);
      }, 0);
    }
    return 0;
  }

  // Metodo generico per calcolare il totale degli extra (fee/assicurazioni)
  private calculateExtraTotal(extraType: string): number {
    if (!this.extra || !this.extra[extraType]) {
      return 0;
    }

    return this.extra[extraType].reduce((total: number, item: any) => {
      return total + item.amountaftertax + (item.adv_markup ? item.adv_markup : 0);
    }, 0);
  }

  // Metodo per calcolare il totale dei voli con sequenceNumber
  private calculateFlightsTotal(): number {
    if (!this.allDays?.flights) {
      return 0;
    }

    const result = this.allDays.flights.reduce(
      (accumulator: { seenSequences: Set<number>; total: number }, flight: any) => {
        const isNewSequence = flight.sequenceNumber === '0' ||
          !accumulator.seenSequences.has(flight.sequenceNumber);

        if (isNewSequence) {
          if (flight.sequenceNumber !== '0') {
            accumulator.seenSequences.add(flight.sequenceNumber);
          }

          const flightAmount = flight.amountaftertax + (flight.adv_markup ? flight.adv_markup : 0);
          return {
            seenSequences: accumulator.seenSequences,
            total: accumulator.total + flightAmount
          };
        }

        return accumulator;
      },
      { seenSequences: new Set<number>(), total: 0 }
    );

    return result.total;
  }

  // Metodo per calcolare le tasse dei voli
  private calculateFlightsTaxTotal(): number {
    if (!this.allDays?.flights) {
      return 0;
    }

    const result = this.allDays.flights.reduce(
      (accumulator: { seenSequences: Set<number>; total: number }, flight: any) => {
        const hasSequence = !accumulator.seenSequences.has(flight.sequenceNumber);
        const hasTaxes = 'taxes' in flight;

        if (hasSequence && hasTaxes) {
          accumulator.seenSequences.add(flight.sequenceNumber);

          return {
            seenSequences: accumulator.seenSequences,
            total: accumulator.total + flight.taxes
          };
        }

        return accumulator;
      },
      { seenSequences: new Set<number>(), total: 0 }
    );

    return result.total;
  }

  // Metodo per calcolare il totale degli hotel
  private calculateHotelsTotal(): number {
    if (!this.allDays?.hotels) {
      return 0;
    }

    return this.allDays.hotels.reduce((total: number, hotel: any) => {
      return total + hotel.hotels[0].rooms.reduce((roomsTotal: number, room: any) => {
        return roomsTotal + room.amountaftertax + (room.adv_markup ? room.adv_markup : 0);
      }, 0);
    }, 0);
  }

  // Metodo per calcolare il totale delle auto
  private calculateCarsTotal(): number {
    if (!this.allDays?.cars) {
      return 0;
    }

    return this.allDays.cars.reduce((total: number, vehicle: any) => {
      return total + vehicle.vehicles.reduce((vehicleTotal: number, car: any) => {
        return vehicleTotal + car.amountaftertax + (car.adv_markup ? car.adv_markup : 0);
      }, 0);
    }, 0);
  }

  // Aggiorna il metodo calculateTotals con il calcolo corretto
  private calculateTotals(): void {
    if (!this.allDays) {
      return;
    }

    // Calcolo totali usando i metodi helper
    const flightsTotal = this.calculateFlightsTotal();
    const flightsTotalTax = this.calculateFlightsTaxTotal();
    const hotelsTotal = this.calculateHotelsTotal();
    const carsTotal = this.calculateCarsTotal();
    const transfersTotal = this.calculateServiceTotal('transfers');
    const transfersSuggestedTotal = this.calculateSuggestedServiceTotal('transfersSuggested');
    const optionalsTotal = this.calculateServiceTotal('optionals');
    const optionalsSuggestedTotal = this.calculateSuggestedServiceTotal('optionalsSuggested');
    const escortedsTotal = this.calculateServiceTotal('escorteds');
    const feeTotal = this.calculateExtraTotal('fees');
    const insuranceTotal = this.calculateExtraTotal('insurances');

    // Assegnazione totali
    this.totalGroundService = hotelsTotal + carsTotal + transfersTotal +
      optionalsTotal + escortedsTotal;
    this.totalGroundNoOptional = hotelsTotal + carsTotal + transfersTotal;
    this.totalFlightsService = flightsTotal;
    this.totalFlightsTaxService = flightsTotalTax;
    this.totalActivities = optionalsTotal + escortedsTotal;
    this.totalFeeInsuranceService = insuranceTotal + feeTotal;
    this.transfersSuggestedTotal = transfersSuggestedTotal;
    this.optionalsSuggestedTotal = optionalsSuggestedTotal;
  }

  // Carica il CSS printStyleUrl nel DOM per l'anteprima
  private loadPrintStyleUrl(): void {
    const url = this.tenant?.printSettings?.printStyleUrl;
    if (!url) { return; }

    // Evita di aggiungere lo stesso link più volte
    const existing = document.querySelector(`link[href="${url}"]`);
    if (existing) { return; }

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = url;
    document.head.appendChild(link);
  }

  // Metodo per calcolare il totale generale
  public getTotalAmount(): number {
    const advFees = (this.data?.advInformations?.feeFlight || 0) +
      (this.data?.advInformations?.feeLandServices || 0);

    return this.totalGroundService +
      this.totalFlightsService +
      advFees +
      this.totalFeeInsuranceService;
  }

  // Metodo per ottenere le informazioni ADV
  public getAdvInformations(): any {
    return this.data?.advInformations || { feeFlight: 0, feeLandServices: 0 };
  }

  public close(): void {
    this.activeModal.close('Closed');
  }

  public async print(): Promise<void> {
    // const contentToPrint = document.getElementById('modal-print-content');
    //
    // if (contentToPrint) {
    //   try {
    //     await this.printService.generatePDF(contentToPrint);
    //     this.activeModal.close('Printing');
    //   } catch (error) {
    //     console.error('Errore durante la stampa:', error);
    //     this.activeModal.close('Error');
    //   }
    // } else {
    //   console.error('Impossibile trovare il contenuto da stampare.');
    //   this.activeModal.close('Error');
    // }
    try {
      const element = document.getElementById('modal-print-content');
      if (!element) {
        throw new Error('Elemento di stampa non trovato');
      }

      // Determina l'URL del CSS personalizzato in base al tenant
      // Cerca una proprietà specifica nel tenant o usa una convenzione basata sul code/consolidator
      let customCssUrl = '';
      if (this.tenant) {
        // Se esiste una proprietà specifica per lo stile di stampa (potrebbe essere aggiunta al json in futuro)
        if (this.tenant.printSettings?.printStyleUrl) {
          customCssUrl = this.tenant.printSettings.printStyleUrl;
        }
        // Fallback alla convenzione: assets/custom/[CONSOLIDATOR]/css/print.css
        // Usa il consolidator se disponibile
        else if (this.tenant.consolidator) {
          customCssUrl = `assets/custom/${this.tenant.consolidator}/css/print.css`;
        }
      }

      await this.printService.generatePDF(element, customCssUrl);

    } catch (error) {
      console.error('Errore durante la stampa:', error);
      // Mostra un messaggio di errore user-friendly
      alert('Si è verificato un errore durante la generazione del PDF. Riprova tra qualche istante.');
    }
  }

  public sendEmail(): void {
    console.log('sendEmail called');
    try {
      const modalRef = this.modalService.open(EmailModalComponent, { size: 'lg', windowClass: 'email-modal', backdrop: 'static' });
      console.log('Modal opened', modalRef);

      // Use bookId from data if available, otherwise try to find it in iter
      console.log('Data received for email modal:', this.data);
      const bookId = this.data?.iter?.riferimento || '';
      const consolidator = this.data?.consolidator || '';
      console.log('bookId for email:', bookId);

      // Construct URL for shared print preview
      const link = `${window.location.protocol}//${window.location.host}/print/${bookId}`;
      // const defaultText = `Ciao, ti invio via mail il pdf da stampare grazie.\n\nte la invio: ${link}`;

      modalRef.componentInstance.bookId = bookId;
      modalRef.componentInstance.pdfUrl = link;
      modalRef.componentInstance.consolidator = consolidator;

      modalRef.result.then((result) => {
        if (result) {
          console.log('Invio email a:', result.to_address);
        }
      }, (reason) => {
        console.log('Modal dismissed:', reason);
      });
    } catch (e) {
      console.error('Error opening email modal:', e);
      alert('Errore nell\'apertura della modale email: ' + e);
    }
  }

  // 1. Metodo per ottenere la prima immagine dal componente overview/block-image
  public getFirstOverviewImage(): string {
    if (this.data?.citiesImages && this.data.citiesImages.length > 0 && this.data.urlCitiesImages) {
      return this.toSecureUrl(this.data.urlCitiesImages + this.data.citiesImages[0]);
    }
    return '';
  }

  // Metodo per ottenere l'immagine di copertina con fallback e URL assoluto
  public getCoverImage(): string {
    const overview = this.getFirstOverviewImage();
    if (overview) return overview;

    if (this.tenant?.printSettings?.coverImage) {
      return this.toAbsoluteUrl(this.tenant.printSettings.coverImage);
    }

    return this.getServiceImageUrl(null);
  }

  // 3. Metodo per raggruppare i giorni per destinazione
  public getGroupedDestinations(): any[] {

    if (!this.days || this.days.length === 0) {
      return [];
    }

    const destinations: any[] = [];
    let currentDestination: any = null;
    let dayCounter = 1;

    this.days.forEach((day: any) => {

      // Verifica se il giorno ha una città
      if (day.city) {
        const cityName = day.city.name;

        // Se è una nuova destinazione o la prima
        if (!currentDestination || currentDestination.name !== cityName) {
          // Salva la destinazione precedente se esiste
          if (currentDestination) {
            destinations.push(currentDestination);
          }

          // Crea una nuova destinazione
          currentDestination = {
            name: cityName,
            description: day.city.description,
            image: day.city.image,
            days: [],
            startDayNumber: dayCounter
          };
        }

        // Aggiungi il giorno alla destinazione corrente (copia senza voli)
        const dayWithoutFlights = { ...day };
        delete dayWithoutFlights.flights; // Rimuovi i voli dal giorno
        currentDestination.days.push(dayWithoutFlights);
        dayCounter++;
      } else {
        // Se non ha città, aggiungi comunque il contatore giorni
        dayCounter++;
      }
    });

    // Aggiungi l'ultima destinazione se esiste
    if (currentDestination) {
      destinations.push(currentDestination);
    }

    return destinations;
  }

  // 4. Metodi per i totali del summary
  public getSummaryFlightsTotal(): number {
    return this.calculateFlightsTotal();
  }

  public getSummaryHotelsTotal(): number {
    return this.calculateHotelsTotal();
  }

  public getSummaryCarsTotal(): number {
    return this.calculateCarsTotal();
  }

  public getSummaryTransfersTotal(): number {
    return this.calculateServiceTotal('transfers');
  }

  public getSummaryOptionalsTotal(): number {
    return this.calculateServiceTotal('optionals');
  }

  public getSummaryEscortedsTotal(): number {
    return this.calculateServiceTotal('escorteds');
  }

  public getSummaryExtraTotal(): number {
    return this.totalFeeInsuranceService;
  }

  public getGrandTotal(): number {
    return this.totalFlightsService + this.totalGroundService + this.totalFeeInsuranceService;
  }

  public getTotal(arr: any): number {
    if (!arr || !Array.isArray(arr)) return 0;
    return arr.map((item: any) => item.amountaftertax + (item.adv_markup ? item.adv_markup : 0))
      .reduce((acc: number, curr: number) => acc + curr, 0);
  }

  // 5. Metodi per le note
  public hasNotes(): boolean {
    return this.getGeneralNotes().length > 0 ||
      this.getCountryNotes().length > 0 ||
      this.getSupplierNotes().length > 0;
  }

  public getGeneralNotes(): any[] {
    const notes: any[] = [];

    // Note dall'iter
    if (this.iter?.notes) {
      notes.push(...this.iter.notes.filter((note: any) => note.type === 'general' || !note.type));
    }

    // Note generali dall'extra
    if (this.extra?.notes) {
      notes.push(...this.extra.notes.filter((note: any) => note.type === 'general' || !note.type));
    }

    return notes;
  }

  public getCountryNotes(): any[] {
    const notes: any[] = [];

    // Note paese dall'iter
    if (this.iter?.notes) {
      notes.push(...this.iter.notes.filter((note: any) => note.type === 'country'));
    }

    // Note paese dall'extra
    if (this.extra?.notes) {
      notes.push(...this.extra.notes.filter((note: any) => note.type === 'country'));
    }

    return notes;
  }

  public getSupplierNotes(): any[] {
    const notes: any[] = [];

    // Note fornitori dai servizi
    if (this.days) {
      this.days.forEach((day: any) => {
        // Note dagli hotel
        if (day.hotels) {
          day.hotels.forEach((hotel: any) => {
            if (hotel.notes) {
              notes.push(...hotel.notes.filter((note: any) => note.type === 'supplier'));
            }
          });
        }

        // Note dai voli
        if (day.flights) {
          day.flights.forEach((flight: any) => {
            if (flight.notes) {
              notes.push(...flight.notes.filter((note: any) => note.type === 'supplier'));
            }
          });
        }

        // Note da altri servizi
        ['transfers', 'optionals', 'escorted', 'vehicles'].forEach(serviceType => {
          if (day[serviceType]) {
            day[serviceType].forEach((service: any) => {
              if (service.notes) {
                notes.push(...service.notes.filter((note: any) => note.type === 'supplier'));
              }
            });
          }
        });
      });
    }

    return notes;
  }

  public getHotelImage(hotel: any): string {
    if (hotel.image) {
      return hotel.image;
    } else if (this.noImageUrl) {
      return this.noImageUrl;
    } else {
      return 'assets/custom/' + sessionStorage.getItem('version') + '/images/logo_tour_operator.png';
    }
  }

  private toAbsoluteUrl(url: string): string {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return this.toSecureUrl(url);
    return this.toSecureUrl(window.location.origin + '/' + url.replace(/^\/+/, ''));
  }

  private toSecureUrl(url: string): string {
    if (url.startsWith('http://') && window.location.protocol === 'https:') {
      return 'https://' + url.substring(7);
    }
    return url;
  }

  public getServiceImageUrl(imageUrl: string | null | undefined): string {
    if (imageUrl) return this.toAbsoluteUrl(imageUrl);
    if (this.noImageUrl) return this.toAbsoluteUrl(this.noImageUrl);
    return this.toAbsoluteUrl(`assets/custom/${sessionStorage.getItem('version')}/images/logo_tour_operator.png`);
  }

  public getFirstImageUrl(items: any[] | null | undefined): string {
    const url = items && items[0] && items[0].image;
    return this.getServiceImageUrl(url);
  }

  public getIconPath(name: string): string | undefined {
    return this.tenant?.printSettings?.icons?.[name];
  }

  public handleImgError(ev: any): void {
    const source = ev.target as HTMLImageElement;
    source.src = this.getServiceImageUrl(null);
  }

  // Metodo per formattare le date nel locale corrente
  public formatDateRange(startDate: string, endDate: string): string {
    const lang = this.translateService.currentLang || 'it';
    const start = parseLocalDate(startDate);
    const end = parseLocalDate(endDate);

    const options: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    };

    if (startDate === endDate) {
      return start.toLocaleDateString(lang, options);
    } else {
      const fromLabel = this.translateService.instant('COMMON.FROM');
      const toLabel = this.translateService.instant('COMMON.TO');
      return `${fromLabel} ${start.toLocaleDateString(lang, options)} ${toLabel} ${end.toLocaleDateString(lang, options)}`;
    }
  }

  // Metodo per ottenere il nome dell'hotel
  public getHotelName(hotel: any): string {
    if (hotel && hotel.hotels && hotel.hotels[0] && hotel.hotels[0].name) {
      return hotel.hotels[0].name;
    }
    return 'Hotel non specificato';
  }

  // Metodo per ottenere la descrizione del veicolo (dal campo description, poi dai commenti/regole)
  public getVehicleDescription(car: any): string {
    const vehicle = car?.vehicles?.[0];
    if (!vehicle) {
      return '';
    }
    if (vehicle.description) {
      return vehicle.description;
    }
    const comments = vehicle.comments || vehicle.rules || [];
    const firstComment = comments.find((c: any) => c.value && c.value.trim());
    return firstComment ? firstComment.value : '';
  }

  // Metodo per formattare le date di check-in e check-out dell'hotel
  public getHotelDates(hotel: any): string {
    if (hotel && hotel.checkin && hotel.checkout) {
      const lang = this.translateService.currentLang || 'it';
      const checkin = parseLocalDate(hotel.checkin);
      const checkout = parseLocalDate(hotel.checkout);

      const formatDate = (date: Date) => {
        return date.toLocaleDateString(lang, { day: '2-digit', month: '2-digit' });
      };

      const fromLabel = this.translateService.instant('COMMON.FROM');
      const toLabel = this.translateService.instant('COMMON.TO');
      return `${fromLabel} ${formatDate(checkin)} ${toLabel} ${formatDate(checkout)}`;
    }
    return '';
  }

  // Metodo per ottenere i nomi dei servizi transfer
  public getTransferName(transfer: any): string {
    if (transfer && transfer.optionals && transfer.optionals[0] && transfer.optionals[0].items && transfer.optionals[0].items.length > 0) {
      return transfer.optionals[0].items[0].name;
    }
    return 'Transfer non specificato';
  }

  // Metodo per ottenere la descrizione dei servizi transfer
  public getTransferDescription(transfer: any): string {
    if (transfer && transfer.optionals && transfer.optionals[0] && transfer.optionals[0].items && transfer.optionals[0].items.length > 0) {
      return transfer.optionals[0].items[0].description || '';
    }
    return '';
  }

  // Metodo per ottenere i nomi dei servizi optional
  public getOptionalName(optional: any): string {
    if (optional && optional.optionals && optional.optionals[0] && optional.optionals[0].items && optional.optionals[0].items.length > 0) {
      return optional.optionals[0].items[0].name;
    }
    return 'Esperienza non specificata';
  }

  // Metodo per ottenere la descrizione dei servizi optional
  public getOptionalDescription(optional: any): string {
    if (optional && optional.optionals && optional.optionals[0] && optional.optionals[0].items && optional.optionals[0].items.length > 0) {
      return optional.optionals[0].items[0].description || '';
    }
    return '';
  }

  // Metodo per ottenere i nomi dei servizi escorted
  public getEscortedName(escorted: any): string {
    if (escorted && escorted.optionals && escorted.optionals[0] && escorted.optionals[0].items && escorted.optionals[0].items.length > 0) {
      return escorted.optionals[0].items[0].name;
    }
    return 'Servizio accompagnato non specificato';
  }

  // Metodo per ottenere la descrizione dei servizi escorted
  public getEscortedDescription(escorted: any): string {
    if (escorted && escorted.optionals && escorted.optionals[0] && escorted.optionals[0].items && escorted.optionals[0].items.length > 0) {
      return escorted.optionals[0].items[0].description || '';
    }
    return '';
  }

  // Metodo per ottenere la descrizione ServiziInclusi da mostrare nella testata del tour
  public getServiziInclusiDescription(escorted: any): string {
    const descriptions = escorted?.optionals?.[0]?.descriptions;
    if (descriptions && Array.isArray(descriptions)) {
      const serviziInclusi = descriptions.find((d: any) => d.code === 'ServiziInclusi');
      return serviziInclusi?.value || '';
    }
    return '';
  }

  // Metodo per ottenere tutti gli items di un servizio (transfers, optionals, escorteds)
  public getServiceItems(service: any): any[] {
    if (service && service.optionals && service.optionals[0] && service.optionals[0].items) {
      return service.optionals[0].items;
    }
    return [];
  }

  public getSupplementsAndReductions(service: any): any[] {
    const source = service?.optionals?.[0]?.supplementsAndReductions
      || service?.optionals?.[0]?.items?.[0]?.supplementsAndReductions
      || service?.supplementsAndReductions
      || service?.items?.[0]?.supplementsAndReductions
      || [];
    return Array.isArray(source) ? source : [];
  }

  public hasSupplementsAndReductions(service: any): boolean {
    return this.getSupplementsAndReductions(service).length > 0;
  }

  // Metodo per ottenere il nome di un servizio
  public getServiceName(service: any): string {
    // Se è un escorted step, usa il title dello step
    if (service && service.step && service.step.title) {
      return service.step.title;
    }
    // Altrimenti usa la logica normale
    if (service && service.optionals && service.optionals[0]) {
      return service.optionals[0].type || 'Servizio non specificato';
    }
    return 'Servizio non specificato';
  }

  // Metodo per ottenere il tipo/descrizione di un servizio
  public getServiceType(service: any): string {
    if (service && service.optionals && service.optionals[0]) {
      return service.optionals[0].type || '';
    }
    return '';
  }

  // Metodo per ottenere la città di un servizio
  public getServiceCity(service: any): string {
    return service?.optionals?.[0]?.city_name || '';
  }

  public getFlightPax(flight: any): number {
    return (flight.adults || 0) + (flight.children || 0) + (flight.infants || 0);
  }

  public getRoomPax(room: any): number {
    return (room.adults || 0) + (room.children || 0) + (room.infants || 0);
  }

  public getTotalServicePax(service: any): number {
    const item = service?.optionals?.[0]?.items?.[0];
    if (!item) {
      return 0;
    }
    const adults = item.adults?.totalNumber || 0;
    const children = item.children?.totalNumber || 0;
    return adults + children;
  }

  public getBookingTotalPax(): number {
    if (!this.iter?.rooms) {
      return 0;
    }
    let total = 0;
    for (const room of this.iter.rooms) {
      total += (room.adults || 0);
      if (room.childrenBirthdate) {
        total += room.childrenBirthdate.length;
      }
    }
    return total;
  }

  // Metodo per ottenere la descrizione di un servizio
  // Le note del servizio vengono anteposte alla description per mantenere uno stile uniforme
  public getServiceDescription(service: any): string {
    const notesHtml = this.getServiceNotesHtml(service);

    // Se è un escorted step, usa le descriptions dello step
    // NOTA: notesHtml non viene preposto perché le note degli escorteds
    // contengono il testo "LA QUOTA COMPRENDE/NON COMPRENDE" già presente
    // nell'header del gruppo (optional.description)
    if (service && service.step && service.step.descriptions && service.step.descriptions.length > 0) {
      return service.step.descriptions.map((desc: any) => desc.text).join('');
    }
    // Altrimenti usa la logica normale
    if (service && service.optionals && service.optionals[0]) {
      const opt = service.optionals[0];
      // Prima cerca il campo description diretto
      if (opt.description) {
        return notesHtml + opt.description;
      }
      // Fallback: cerca la voce "Descrizione" negli items
      if (opt.items && Array.isArray(opt.items)) {
        for (const item of opt.items) {
          if (item.descriptions && Array.isArray(item.descriptions)) {
            const descEntry = item.descriptions.find((d: any) => d.name === 'Descrizione');
            if (descEntry && descEntry.value) {
              return notesHtml + descEntry.value;
            }
          }
        }
      }
      // Fallback finale: usa il nome o il tipo del servizio
      return notesHtml || opt.name || opt.type || '';
    }
    return notesHtml || '';
  }

  // Metodo helper per concatenare le note di un servizio filtrate per stato
  public getServiceNotesHtml(service: any): string {
    if (!service || !service.notes || !Array.isArray(service.notes)) {
      return '';
    }
    let validNotes = this.filterEmptyNotes(service.notes);
    if (validNotes.length === 0) {
      return '';
    }
    const suffix = this.state === 'Confermata' ? 'itinerario_conferma' : 'itinerario_preventivo';
    validNotes = validNotes.filter((note: any) => note.id && note.id.endsWith(suffix));
    if (validNotes.length === 0) {
      return '';
    }
    return validNotes.map((note: any) => note.text).join('');
  }

  // Metodo per verificare se un servizio ha items
  public hasServiceItems(service: any): boolean {
    return this.getServiceItems(service).length > 0;
  }

  // Metodo per ottenere l'immagine di un escorted step
  public getEscortedStepPhoto(service: any): string {
    return this.getServiceImageUrl(service?.step?.photo);
  }

  // Metodo per verificare se un escorted ha una foto
  public hasEscortedStepPhoto(service: any): boolean {
    if (service && service.step && service.step.photo && service.step.photo.trim() !== '') {
      return true;
    }
    return !!this.noImageUrl;
  }

  // Metodo per ottenere le date formattate di un escorted step
  public getEscortedStepDateRange(service: any): string {
    if (service && service.step && service.step.dateFrom && service.step.dateTo) {
      return this.formatDateRange(service.step.dateFrom, service.step.dateTo);
    }
    return '';
  }

  // Metodo per verificare se un servizio è un escorted step
  public isEscortedStep(service: any): boolean {
    return service && service.step;
  }

  // Marca la visibilità dell'header degli escorted: il primo incontro di un sequenceNumber
  // mostra l'header, le tappe successive mostrano solo gli step
  private markEscortedHeaderVisibility(): void {
    const seenSequenceNumbers = new Set<string>();
    for (const tappa of this.tappe || []) {
      for (const escorted of tappa.services?.escorteds || []) {
        const sequenceNumber = escorted.sequenceNumber || escorted.optionals?.[0]?.code || escorted.optionals?.[0]?.name;
        if (sequenceNumber) {
          escorted._showHeader = !seenSequenceNumbers.has(sequenceNumber);
          seenSequenceNumbers.add(sequenceNumber);
        } else {
          escorted._showHeader = true;
        }
      }
    }
  }

  // Aggiungi questi metodi per gestire city_description e city_photo

  // Metodo per ottenere la descrizione della città
  public getCityDescription(tappa: Tappa): string {
    // Prendi la descrizione dal primo giorno della tappa che la contiene
    for (const day of tappa.days) {
      if (day.city_description) {
        return day.city_description;
      }
    }
    return '';
  }

  // Metodo per ottenere la foto della città
  public getCityPhoto(tappa: Tappa): string {
    // Prendi la foto dal primo giorno della tappa che la contiene
    for (const day of tappa.days) {
      if (day.city_photo && this.data?.urlCitiesImages) {
        return this.toSecureUrl(this.data.urlCitiesImages + day.city_photo);
      }
    }
    return this.getServiceImageUrl(null);
  }

  // Metodo per verificare se la tappa ha informazioni sulla città
  public hasCityInfo(tappa: Tappa): boolean {
    return this.getCityDescription(tappa) !== '' || this.getCityPhoto(tappa) !== '';
  }

  // Metodo per inizializzare le note
  private initializeNotes(): void {
    this.showGeneralNotes = this.tenant?.showGeneralNotes || false;

    if (this.showGeneralNotes) {
      this.generalNotes = this.data?.iter?.generalNotes || [];
      this.countryNotes = this.data?.iter?.countryNotes || [];
      this.supplierNotes = this.data?.iter?.supplierNotes || [];
    }
  }

  // Filtra le note vuote (stessa logica di notes.component.ts)
  private filterEmptyNotes(notes: any[]): any[] {
    if (!notes || !Array.isArray(notes)) {
      return [];
    }
    const emptyDivPattern = /^<div><br\s*\/?>\s*<\/div>$/i;
    return notes.filter(note => {
      if (!note || !note.text) { return false; }
      const cleanText = note.text.trim();
      return cleanText !== '' && !emptyDivPattern.test(cleanText);
    });
  }

  // Metodo per verificare se ci sono note da mostrare
  public hasNotesToShow(): boolean {
    if (!this.showGeneralNotes) return false;
    return this.getFilteredGeneralNotes().length > 0 ||
      this.getFilteredCountryNotes().length > 0 ||
      this.getFilteredSupplierNotes().length > 0;
  }

  // Metodi per filtrare le note per tipo (con filtro testo vuoto)
  public getFilteredGeneralNotes(): any[] {
    return this.filterEmptyNotes(this.generalNotes).filter(note => note.type === 'NEWSHEET');
  }

  public getFilteredCountryNotes(): any[] {
    return this.filterEmptyNotes(this.countryNotes).filter(note => note.type === 'NEWSHEET');
  }

  public getFilteredSupplierNotes(): any[] {
    return this.filterEmptyNotes(this.supplierNotes).filter(note => note.type === 'NEWSHEET');
  }

  public getAbsoluteTourOperatorLogo(): string {
    if (!this.logoTourOperator) {
      return '';
    }

    // Se è già un URL assoluto, ritornalo così com'è
    if (this.logoTourOperator.startsWith('http://') || this.logoTourOperator.startsWith('https://')) {
      return this.logoTourOperator;
    }

    // Altrimenti costruisci l'URL assoluto
    const baseUrl = window.location.origin;
    const cleanPath = this.logoTourOperator.startsWith('/') ? this.logoTourOperator : '/' + this.logoTourOperator;

    return `${baseUrl}${cleanPath}`;
  }

  public hotelHasMoreThanOneRoom(rooms: any): boolean {
    return rooms.length > 1;
  }
}
